import {
  format,
  addDays,
  subDays,
  isBefore,
  startOfMonth,
  lastDayOfMonth,
  addHours,
  addMinutes,
  differenceInCalendarDays,
  setHours,
  startOfToday,
  setMinutes,
  isAfter,
  endOfMonth,
} from 'date-fns';

/**
 * Encodes a date for use in a URL parameter.
 * @param date - The date to encode.
 * @returns A URL-safe encoded date string.
 */
// export const encodeDateForUrl = (date: Date): string => {
//   const isoString = date.toISOString(); // Converts to ISO 8601 format
//   return encodeURIComponent(isoString); // Encodes for URL safety
// };
export const encodeDateForUrl = (date: Date): string => {
  const formattedDate = format(date, 'yyyy-MM-dd'); // Ensures local time format
  return encodeURIComponent(formattedDate);
};

export const formatDate = (selectedDate: Date | null): string => {
  return selectedDate !== null
    ? format(selectedDate, 'EEE, do MMMM yyyy')
    : 'Select a date';
};

export const getNextDay = (selectedDate: Date | null): Date | null => {
  return selectedDate ? addDays(selectedDate, 1) : null;
};

export const getPreviousDay = (selectedDate: Date | null): Date | null => {
  return selectedDate ? subDays(selectedDate, 1) : null;
};

export function formatStartTime(time?: string): string {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) {
    return `1900-01-01 00:00.000`;
    // throw new Error('Invalid time format');
  }
  return `1900-01-01 ${time}:00.000`;
}
export function formatCurrentTime(time?: string): string {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) {
    return `1900-01-01 00:00:00.000`;
  }

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const dd = String(today.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd} ${time}:00.000`;
}

export function validateDateRange(fromDate: Date, toDate: Date): boolean {
  if (isBefore(fromDate, toDate)) {
    return false;
  }
  return true;
}

/**
 * Returns the first and last date of the next month based on the given date.
 * Handles transition to the next year if the current month is December.
 * @param currentMonth - The current month as a Date object.
 * @param dateFormat - The format string for the date (optional).
 * @returns An object with formatted first and last date of the next month.
 * @example
 * const { firstDate, lastDate } = getNextMonthDates(new Date(), 'yyyy-MM-dd');
 * console.log(firstDate, lastDate); // e.g., "2024-11-01", "2024-11-30"
 */
export const getFirstAndLastDate = (
  currentMonth: Date,
  dateFormat: string = 'yyyy-MM-dd HH:mm',
) => {
  // Get the first and last date of the month
  const firstDate = startOfMonth(currentMonth);
  const lastDate = addMinutes(addHours(lastDayOfMonth(currentMonth), 23), 59); // Set to 23:59

  // Return formatted first and last date
  return {
    firstDate: format(firstDate, dateFormat), // 00:00
    lastDate: format(lastDate, dateFormat), // 23:59
  };
};

export function formatTimeToHHMM(datetime: string): string {
  try {
    const date = new Date(datetime);
    return format(new Date(date), 'hh:mm a');
    // return date.toISOString().substring(11, 16); // Extracts HH:mm
  } catch (error) {
    console.error('Invalid date:', datetime);
    return '';
  }
}

export function getDaysFromToday(targetDate: string | Date): number {
  const today = new Date();
  const date = new Date(targetDate); // ensures compatibility with both string and Date input
  return differenceInCalendarDays(date, today);
}


/**
 * Returns today's date or tomorrow's date if current time is after 10 AM.
 */
export const getAdjustedDate = (): Date => {
  const now = new Date();
  const tenAM = setMinutes(setHours(startOfToday(), 10), 0);

  return isAfter(now, tenAM) ? addDays(startOfToday(), 1) : startOfToday();
};

export function formatDateToDDMMYYYY(date: Date): string {
  return format(date, 'dd/MM/yyyy');
}


export function getFirstAndLastDateForCalender(date: Date): {
  firstDate: string;
  lastDate: string;
} {
  const firstDate = new Date(date.getFullYear(), date.getMonth(), 1).toLocaleDateString('en-CA');
  const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0).toLocaleDateString('en-CA');
  return { firstDate, lastDate };
}


/**
 * Returns the first day of the current month.
 */
export const getFirstDayOfMonth = (): Date => {
  return startOfMonth(new Date());
};

/**
 * Returns the last day of the current month.
 */
export const getLastDayOfMonth = (): Date => {
  return endOfMonth(new Date());
};